import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { FiAlertCircle, FiCalendar, FiFileText, FiRefreshCw, FiTarget, FiTrendingUp } from 'react-icons/fi';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import NextStepsCard from '../components/interview/NextStepsCard';
import ScoreCard from '../components/interview/ScoreCard';
import StatusBadge from '../components/interview/StatusBadge';
import { useInterview } from '../context/InterviewContext';
import { getUserFacingError, UserFacingErrorKind } from '../services/http';
import { interviewService } from '../services/interviewService';
import { readLatestInterviewCacheForJob } from '../services/sessionStorage';
import { resolveInterviewTimestampMeta } from '../utils/interviewTimestamp';
import {
  FinalizationSnapshot,
  finishInterviewWithRecovery,
  pollFinalizationUntilTerminal,
} from '../utils/interviewFinalizationRecovery';

interface Result {
  id: number;
  interview_id: number;
  final_score: number;
  content_final_score?: number | null;
  confidence_score?: number | null;
  confidence_coverage_pct?: number;
  confidence_weight_pct?: number;
  is_provisional?: boolean;
  confidence_recomputed_at?: string | null;
  technical_score: number;
  communication_score: number;
  problem_solving_score: number;
  status: 'strong' | 'average' | 'weak';
  summary: string;
  strength: string;
  weakness: string;
  recommendation?: string;
}

interface InterviewResultMeta {
  id: number;
  status: string;
  created_at?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
}

interface ProcessingStatus {
  state?: 'processing' | 'ready' | 'failed' | 'technical_interrupted';
  ready: boolean;
  failed?: boolean;
  interrupted?: boolean;
  retryable?: boolean;
  input_closed?: boolean;
  error_code?: string | null;
  percent: number;
  message?: string;
  stages?: {
    answers?: { total?: number; completed?: number; pending?: number; failed?: number };
    confidence?: { total?: number; completed?: number; pending?: number; failed?: number };
    final_result?: { completed?: boolean; provisional?: boolean };
  };
}

type ResultViewState = 'loading' | 'processing' | 'ready' | 'failed' | 'interrupted' | 'empty' | 'unauthorized' | 'not_found' | 'error';
type Category = 'technical' | 'communication' | 'problem_solving';

const CATEGORY_LABEL: Record<Category, string> = {
  technical: 'Teknikal',
  communication: 'Komunikasi',
  problem_solving: 'Problem Solving',
};

const STATUS_HEADLINE: Record<Result['status'], string> = {
  strong: 'Sesi ini menunjukkan kesiapan yang kuat untuk lanjut ke tahap berikutnya.',
  average: 'Fondasinya sudah ada, dengan beberapa area yang masih bisa diperdalam.',
  weak: 'Masih ada beberapa area inti yang perlu diperkuat dari jawaban sesi ini.',
};

const STATUS_TONE: Record<Result['status'], string> = {
  strong: 'Hasil ini menunjukkan konsistensi jawaban yang baik di beberapa area penting.',
  average: 'Ada sinyal positif, tetapi kualitas jawaban masih belum merata di seluruh kategori.',
  weak: 'Perlu latihan lebih terarah agar jawaban terasa lebih konkret, meyakinkan, dan terstruktur.',
};

const getWeakestCategory = (
  technical: number,
  communication: number,
  problemSolving: number
): Category => {
  const scores: Record<Category, number> = {
    technical,
    communication,
    problem_solving: problemSolving,
  };

  return (Object.keys(scores) as Category[]).reduce((left, right) =>
    scores[left] <= scores[right] ? left : right
  );
};

const getStrongestCategory = (
  technical: number,
  communication: number,
  problemSolving: number
): Category => {
  const scores: Record<Category, number> = {
    technical,
    communication,
    problem_solving: problemSolving,
  };

  return (Object.keys(scores) as Category[]).reduce((left, right) =>
    scores[left] >= scores[right] ? left : right
  );
};

const InterviewResult: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { interviewData, bindInterviewScope, resetInterview, setResult: persistResult } = useInterview();

  const routeJobId = Number(jobId ?? 0);
  const fallbackCache = useMemo(
    () => readLatestInterviewCacheForJob<{
      interviewId?: number | null;
      jobId?: number | null;
      outsourceId?: number | null;
      result?: Result | null;
    }>(routeJobId),
    [routeJobId]
  );

  const scopedJobId = Number(state?.jobId ?? interviewData.jobId ?? fallbackCache?.jobId ?? jobId ?? 0);
  const scopedOutsourceId = Number(state?.outsourceId ?? interviewData.outsourceId ?? fallbackCache?.outsourceId ?? 0);

  const interviewId: number = state?.interviewId ?? interviewData.interviewId ?? fallbackCache?.interviewId ?? 0;
  const cachedResult = interviewId && interviewData.interviewId === interviewId
    ? interviewData.result as Result | null
    : fallbackCache?.interviewId === interviewId
    ? fallbackCache.result ?? null
    : null;

  const [result, setResult] = useState<Result | null>(cachedResult);
  const [interviewInfo, setInterviewInfo] = useState<InterviewResultMeta | null>(null);
  const [processingStatus, setProcessingStatus] = useState<ProcessingStatus | null>(null);
  const [viewState, setViewState] = useState<ResultViewState>(result ? 'ready' : 'loading');
  const [error, setError] = useState('');
  const [errorRetryable, setErrorRetryable] = useState(false);
  const [pollGeneration, setPollGeneration] = useState(0);
  const [pollingExhausted, setPollingExhausted] = useState(false);
  const [retryingFinalization, setRetryingFinalization] = useState(false);
  const resultRef = useRef<Result | null>(cachedResult);
  const appliedResultIdRef = useRef<number | null>(cachedResult?.id ?? null);

  useEffect(() => {
    resultRef.current = result;
  }, [result]);

  useEffect(() => {
    if (!scopedJobId || !scopedOutsourceId) return;
    bindInterviewScope({ jobId: scopedJobId, outsourceId: scopedOutsourceId });
  }, [bindInterviewScope, scopedJobId, scopedOutsourceId]);

  const applyErrorState = useCallback((kind: UserFacingErrorKind, message: string) => {
    setErrorRetryable(kind === 'network' || kind === 'server' || kind === 'unknown');

    if (kind === 'auth') {
      setViewState('unauthorized');
      setError(message);
      return;
    }

    if (kind === 'not_found') {
      setViewState('not_found');
      setError(message);
      return;
    }

    setViewState('error');
    setError(message);
  }, []);

  const applyFinalizationSnapshot = useCallback((snapshot: FinalizationSnapshot) => {
    if (snapshot.interview) setInterviewInfo(snapshot.interview);
    setProcessingStatus(snapshot.processingStatus);

    if (snapshot.interrupted) {
      setResult(null);
      setError('Interview ini dihentikan karena kendala teknis dan tidak memiliki hasil evaluasi.');
      setErrorRetryable(false);
      setViewState('interrupted');
      return;
    }

    if (snapshot.failed) {
      setResult(null);
      setError(snapshot.processingStatus?.message || 'Pemrosesan hasil interview gagal.');
      setErrorRetryable(snapshot.retryable);
      setViewState('failed');
      return;
    }

    if (!snapshot.ready) {
      setResult(null);
      setViewState('processing');
      return;
    }

    if (!snapshot.result) {
      setResult(null);
      setError('Status hasil sudah selesai, tetapi data hasil belum tersedia.');
      setErrorRetryable(true);
      setViewState('error');
      return;
    }

    const nextResult = snapshot.result as Result;
    setResult(nextResult);
    if (appliedResultIdRef.current !== nextResult.id) {
      appliedResultIdRef.current = nextResult.id;
      persistResult(nextResult);
    }
    setError('');
    setViewState('ready');
  }, [persistResult]);

  useEffect(() => {
    let cancelled = false;

    if (!interviewId) {
      setViewState('empty');
      setError('Sesi interview tidak ditemukan.');
      return undefined;
    }

    setPollingExhausted(false);
    setError('');
    if (!resultRef.current) setViewState('loading');

    void pollFinalizationUntilTerminal(
      () => interviewService.getResult(interviewId),
      {
        maxPolls: 60,
        maxNetworkErrors: 3,
        delayMs: 2000,
        isCancelled: () => cancelled,
        onSnapshot: (snapshot) => {
          if (!cancelled) applyFinalizationSnapshot(snapshot);
        },
      }
    ).then((outcome) => {
      if (cancelled || outcome.kind === 'cancelled') return;
      if (outcome.kind === 'exhausted') {
        setPollingExhausted(true);
        setViewState('processing');
        return;
      }
      if (outcome.kind === 'network_exhausted') {
        const userFacingError = getUserFacingError(outcome.error, 'Koneksi ke pemrosesan hasil terputus.');
        applyErrorState(userFacingError.kind, userFacingError.message);
      }
    }).catch((err) => {
      if (cancelled) return;
      const userFacingError = getUserFacingError(err, 'Gagal memuat hasil interview.');
      applyErrorState(userFacingError.kind, userFacingError.message);
    });

    return () => {
      cancelled = true;
    };
  }, [applyErrorState, applyFinalizationSnapshot, interviewId, pollGeneration]);

  const restartPolling = useCallback(() => {
    setPollGeneration((current) => current + 1);
  }, []);

  const retryFinalization = useCallback(async () => {
    if (!interviewId || retryingFinalization) return;
    setRetryingFinalization(true);
    setError('');
    try {
      await finishInterviewWithRecovery(
        () => interviewService.finish(interviewId, 0),
        () => interviewService.getProcessing(interviewId),
        {
          maxFinishAttempts: 1,
          maxReconcilePolls: 3,
          reconcileDelayMs: 1200,
        }
      );
      restartPolling();
    } catch (err) {
      const userFacingError = getUserFacingError(err, 'Pemrosesan hasil belum dapat dicoba kembali.');
      applyErrorState(userFacingError.kind, userFacingError.message);
    } finally {
      setRetryingFinalization(false);
    }
  }, [applyErrorState, interviewId, restartPolling, retryingFinalization]);

  const handleGoDetail = () => {
    navigate(`/interview/detail/${jobId}`, {
      state: { interviewId, outsourceId: state?.outsourceId, jobId: state?.jobId },
    });
  };

  const handleClose = () => {
    resetInterview();
    navigate('/lowongan-kerja');
  };

  const processingPercentValue = Number(processingStatus?.percent);
  const processingPercent = Number.isFinite(processingPercentValue)
    ? Math.max(0, Math.min(100, Math.round(processingPercentValue)))
    : 0;
  const processingMessage = pollingExhausted
    ? 'Pemeriksaan otomatis dijeda karena hasil membutuhkan waktu lebih lama. Muat ulang untuk memeriksa status terbaru.'
    : processingStatus?.message?.trim() || 'Hampir selesai, mohon tetap di halaman ini.';

  const renderState = (copy: string, actionLabel: string, action: () => void) => (
    <div className="iv-result iv-result--state">
      <div className="iv-result__state-shell">
        <p>{copy}</p>
        <button className="iv-btn iv-btn--outline" onClick={action}>
          {actionLabel}
        </button>
      </div>
    </div>
  );

  if (viewState === 'loading') {
    return (
      <div className="iv-result iv-result--state">
        <div className="iv-result__state-shell iv-result__state-shell--loading">
          <Spinner animation="border" />
          <p>Memuat hasil evaluasi...</p>
        </div>
      </div>
    );
  }

  if (viewState === 'processing') {
    return (
      <div className="iv-result iv-result--state">
        <div className="iv-result__state-shell iv-result__state-shell--processing">
          <div className="iv-result__processing-icon" aria-hidden="true">
            <FiFileText />
            <span />
          </div>
          <div className="iv-result__processing-copy">
            <h1>Sedang menyiapkan hasil interview</h1>
            <p>Kami sedang menganalisis jawaban dan menghitung hasil interview kamu. Proses ini biasanya membutuhkan beberapa saat.</p>
          </div>
          <div
            className="iv-result__processing-progress"
            role="progressbar"
            aria-label={`Progress hasil interview ${processingPercent}%`}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={processingPercent}
          >
            <div className="iv-result__processing-meta">
              <span>Menghitung hasil interview</span>
              <strong>{processingPercent}%</strong>
            </div>
            <div className="iv-result__processing-track">
              <span
                className="iv-result__processing-fill"
                style={{ width: `${processingPercent}%` }}
              />
            </div>
          </div>
          <p className="iv-result__processing-status" aria-live="polite">{processingMessage}</p>
          <button
            className="iv-result__processing-retry"
            type="button"
            aria-label="Muat ulang hasil interview"
            onClick={restartPolling}
          >
            <FiRefreshCw aria-hidden="true" />
            <span>Muat ulang hasil</span>
          </button>
        </div>
      </div>
    );
  }

  if (viewState === 'unauthorized') {
    return renderState(error || 'Sesi login Anda sudah berakhir.', 'Login Lagi', () => navigate('/login'));
  }

  if (viewState === 'not_found') {
    return renderState(error || 'Hasil interview tidak ditemukan.', 'Kembali ke Lowongan', () => navigate('/lowongan-kerja'));
  }

  if (viewState === 'failed') {
    return renderState(
      error || 'Pemrosesan hasil interview gagal.',
      errorRetryable ? (retryingFinalization ? 'Memproses...' : 'Coba Proses Lagi') : 'Kembali ke Lowongan',
      errorRetryable ? retryFinalization : () => navigate('/lowongan-kerja')
    );
  }

  if (viewState === 'interrupted') {
    return renderState(
      error || 'Interview ini dihentikan karena kendala teknis.',
      'Kembali ke Status Lamaran',
      () => navigate('/status-lamaran')
    );
  }

  if (viewState === 'error') {
    return renderState(
      error || 'Gagal memuat hasil interview.',
      errorRetryable ? 'Coba Lagi' : 'Kembali ke Lowongan',
      errorRetryable ? restartPolling : () => navigate('/lowongan-kerja')
    );
  }

  if (viewState === 'empty' || !result) {
    return renderState('Hasil interview belum tersedia.', 'Kembali ke Lowongan', () => navigate('/lowongan-kerja'));
  }

  const interviewTimestamp = resolveInterviewTimestampMeta(interviewInfo);
  const strongestCategory = getStrongestCategory(
    result.technical_score,
    result.communication_score,
    result.problem_solving_score
  );
  const weakestCategory = getWeakestCategory(
    result.technical_score,
    result.communication_score,
    result.problem_solving_score
  );

  return (
    <div className="iv-result">
      <div className="iv-result__shell">
        <header className="iv-result__header">
          <div className="iv-result__header-copy">
            <span className="iv-result__eyebrow">Laporan Interview</span>
            <div className="iv-result__header-main">
              <div>
                <h1 className="iv-result__title">Hasil AI Interview</h1>
                {interviewTimestamp ? (
                  <div className="iv-result__meta">
                    <FiCalendar aria-hidden="true" />
                    <span>{interviewTimestamp.label}: <strong>{interviewTimestamp.display}</strong></span>
                  </div>
                ) : null}
              </div>
              <StatusBadge status={result.status} />
            </div>
          </div>
        </header>

        {result.is_provisional ? (
          <section className="iv-result__notice iv-result__notice--provisional">
            <div className="iv-result__notice-icon"><FiAlertCircle aria-hidden="true" /></div>
            <div>
              <h3>Skor Sementara</h3>
              <p>Confidence signal suara masih diproses. Nilai akhir akan diperbarui otomatis setelah analisis selesai.</p>
            </div>
          </section>
        ) : null}

        <section className="iv-result__hero">
          <div className="iv-result__score-panel">
            <div className="iv-result__score-orb">
              <span className="iv-result__score-number">{result.final_score}</span>
              <span className="iv-result__score-label">Skor Akhir</span>
            </div>
          </div>

          <div className="iv-result__hero-copy">
            <span className="iv-result__hero-kicker">Evaluasi sesi</span>
            <h2 className="iv-result__hero-title">{STATUS_HEADLINE[result.status]}</h2>
            <p className="iv-result__hero-description">{STATUS_TONE[result.status]}</p>

            <div className="iv-result__hero-meta">
              <div className="iv-result__hero-meta-card">
                <span className="iv-result__hero-meta-icon"><FiTrendingUp aria-hidden="true" /></span>
                <div>
                  <span className="iv-result__hero-meta-label">Kategori terkuat</span>
                  <strong>{CATEGORY_LABEL[strongestCategory]}</strong>
                </div>
              </div>
              <div className="iv-result__hero-meta-card">
                <span className="iv-result__hero-meta-icon"><FiTarget aria-hidden="true" /></span>
                <div>
                  <span className="iv-result__hero-meta-label">Fokus berikutnya</span>
                  <strong>{CATEGORY_LABEL[weakestCategory]}</strong>
                </div>
              </div>
              <div className="iv-result__hero-meta-card">
                <span className="iv-result__hero-meta-icon"><FiFileText aria-hidden="true" /></span>
                <div>
                  <span className="iv-result__hero-meta-label">Sinyal hasil</span>
                  <strong>{result.status === 'strong' ? 'Siap dilanjutkan' : result.status === 'average' ? 'Masih bisa diperdalam' : 'Perlu penguatan'}</strong>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="iv-result__band">
          <div className="iv-result__band-header">
            <div>
              <h3>Skor per area</h3>
              <p>Setiap kategori menunjukkan area yang paling menonjol dan yang masih bisa diperkuat.</p>
            </div>
          </div>

          <div className="iv-result__breakdown">
            <ScoreCard label="Teknikal (40%)" score={result.technical_score} />
            <ScoreCard label="Problem Solving (35%)" score={result.problem_solving_score} />
            <ScoreCard label="Komunikasi (25%)" score={result.communication_score} />
          </div>
        </section>

        <section className="iv-result__section iv-result__section--summary">
          <div className="iv-result__section-head">
            <span className="iv-result__section-icon"><FiFileText aria-hidden="true" /></span>
            <div>
              <h3>Ringkasan Sesi</h3>
              <p>Gambaran umum dari jawaban yang kamu berikan sepanjang interview.</p>
            </div>
          </div>
          <p className="iv-result__section-body">{result.summary}</p>
        </section>

        <section className="iv-result__insight-grid">
          {result.strength ? (
            <div className="iv-result__section iv-result__section--strength">
              <div className="iv-result__section-head">
                <span className="iv-result__section-icon"><FiTrendingUp aria-hidden="true" /></span>
                <div>
                  <h3>Kelebihan yang Terlihat</h3>
                  <p>Area yang sudah terasa cukup kuat dari jawabanmu.</p>
                </div>
              </div>
              <p className="iv-result__section-body">{result.strength}</p>
            </div>
          ) : null}

          {result.weakness ? (
            <div className="iv-result__section iv-result__section--weakness">
              <div className="iv-result__section-head">
                <span className="iv-result__section-icon"><FiTarget aria-hidden="true" /></span>
                <div>
                  <h3>Area Peningkatan</h3>
                  <p>Bagian yang masih bisa diperdalam untuk sesi berikutnya.</p>
                </div>
              </div>
              <p className="iv-result__section-body">{result.weakness}</p>
            </div>
          ) : null}
        </section>

        <NextStepsCard
          technicalScore={result.technical_score}
          communicationScore={result.communication_score}
          problemSolvingScore={result.problem_solving_score}
          strength={result.strength}
          weakness={result.weakness}
          recommendation={result.recommendation}
          onBrowseJobs={handleClose}
        />

        <div className="iv-result__actions">
          <button className="iv-btn iv-btn--outline" onClick={handleGoDetail}>
            Lihat Detail Jawaban
          </button>
          <button className="iv-btn iv-btn--primary" onClick={handleClose}>
            Kembali ke Beranda
          </button>
        </div>
      </div>
    </div>
  );
};

export default InterviewResult;
